import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";

import moment from "moment";

import WPAgGrid from "../../packages/WPAgGrid/WPAgGrid";
import WPLoader from "../../packages/WPLoader";

import Charts from "../../FileUpload/chart";

import { FOULING_FAILURE_MODES } from "../../constants/global.constants";

import IconWithTooltip from "../../common/styles/components/headings/IconWithTooltip";

import { dateComparator, getSortedData } from "../../utils/tableDataFormatter";
import { getIndex } from "../../utils/Utils";

import { getFoulingInformation } from "../../network/cipAPI";

import { storeFouling } from "../../Store/TemplateStore/template.action";

import "./index.scss";

const FoulingIdentification = ({ activeStep }) => {
  const { t } = useTranslation();
  const foulingTableRef = useRef();

  const {
    fouling,
    foulingType,
    cleanData: cleaningResponse,
    selTrain: selectedTrain,
    cleanDate: date,
    cips,
    cleaningEffectinessThreshold: threshold,
  } = useSelector((state) => state);

  const [rowData, setRowData] = useState(fouling);
  const [isLoading, setIsLoading] = useState(false);

  const currentYear = useMemo(() => new Date().getFullYear(), []);

  const getReasonValue = (params, isPrimary) => {
    const data = params.data?.["data"];
    if (!data || data.length === 0) {
      return t("foulingStrings.noReason");
    }
    const { primary_reason, proportion1, secondary_reason, proportion2 } =
      data[0];
    const type = isPrimary ? primary_reason : secondary_reason;
    if (!type) return "-";
    const foulingTypeData = foulingType?.[type] || type;
    const propValue = parseFloat(isPrimary ? proportion1 : proportion2);
    const proportion =
      propValue > 0
        ? propValue < 10 && propValue % 1 !== 0
          ? `${propValue.toFixed(1)}%`
          : `${Math.round(propValue)}%`
        : "-";

    return `${foulingTypeData} (${proportion})`;
  };

  const getReasonCellClass = (params, isPrimary) => {
    const data = params.data?.["data"];
    if (!data || data.length === 0) return "";
    const { primary_reason, secondary_reason } = data[0];
    const type = isPrimary ? primary_reason : secondary_reason;
    return FOULING_FAILURE_MODES.includes(type) ? "failure-mode" : "";
  };

  const typeFormatterObject = (params) => {
    const { start_date } = params.data;
    return params.data["data"][0]["type"] === "C"
      ? t("foulingStrings.cip")
      : start_date
        ? t("foulingStrings.shutdown")
        : "-";
  };

  const dateFormatterStartDate = (params) => {
    try {
      const startDate = params.data["start_date"];
      if (startDate) {
        return moment(startDate).format("DD/MM/YYYY");
      }
      return t("ValueStrings.inprogress");
    } catch (err) {
      return "-";
    }
  };

  const dateFormatterEndDate = (params) => {
    try {
      const end_date = params.data["end_date"];
      if (end_date) {
        return moment(end_date).format("DD/MM/YYYY");
      }
      return "-";
    } catch (err) {
      return "-";
    }
  };

  const dispatch = useDispatch();

  const colDefs = [
    {
      headerName: t("FoulingTableStrings.index"),
      valueGetter: (params) => getIndex(params, params.data.start_date),
      flex: 0.5,
    },
    {
      headerName: t("FoulingTableStrings.type"),
      field: "type",
      valueGetter: typeFormatterObject,
      flex: 0.5,
    },
    {
      headerName: t("FoulingTableStrings.start_date"),
      field: "start_date",
      sortable: true,
      flex: 1,
      valueFormatter: dateFormatterStartDate,
      comparator: dateComparator,
    },
    {
      headerName: t("FoulingTableStrings.end_date"),
      field: "end_date",
      sortable: true,
      flex: 1,
      valueFormatter: dateFormatterEndDate,
      comparator: dateComparator,
    },
    {
      headerName: t("FoulingTableStrings.primary_reason"),
      field: "data",
      sortable: true,
      flex: 1.5,
      valueGetter: (params) => getReasonValue(params, true),
      cellClass: (params) => getReasonCellClass(params, true),
    },
    {
      headerName: t("FoulingTableStrings.secondary_reason"),
      field: "data",
      sortable: true,
      flex: 1.5,
      valueGetter: (params) => getReasonValue(params, false),
      cellClass: (params) => getReasonCellClass(params, false),
    },
  ];

  useEffect(() => {
    const fetchData = async () => {
      try {
        setIsLoading(!rowData);
        const res = await getFoulingInformation(selectedTrain);

        if (res && res.status === 200 && res?.data?.fouling) {
          const data =
            typeof res.data.fouling === "string"
              ? JSON.parse(res.data.fouling)
              : res.data.fouling;

          const groupedData = [];

          if (data.cip && Object.keys(data.cip).length > 0) {
            for (const key in data.cip) {
              if (data.cip.hasOwnProperty(key)) {
                const date = data.cip[key]["sd"];
                // Check if the date exists in groupedData
                const existingDateIndex = groupedData.findIndex(
                  (group) => group.date === date,
                );

                // If the date is found, add the data to the existing group
                if (existingDateIndex !== -1) {
                  groupedData[existingDateIndex].data.push({
                    type: data.cip[key]["type"],
                    start_date: data.cip[key]["sd"],
                    end_date: data.cip[key]["ed"],
                    primary_reason: data.primary_reason[key],
                    proportion1: data.proportion1[key],
                    secondary_reason: data.secondary_reason[key],
                    proportion2: data.proportion2[key],
                  });
                } else {
                  // If the date is not found, create a new group
                  groupedData.push({
                    date,
                    start_date: data.cip[key]["sd"],
                    end_date: data.cip[key]["ed"],
                    data: [
                      {
                        type: data.cip[key]["type"],
                        start_date: data.cip[key]["sd"],
                        end_date: data.cip[key]["ed"],
                        primary_reason: data.primary_reason[key],
                        proportion1: data.proportion1[key],
                        secondary_reason: data.secondary_reason[key],
                        proportion2: data.proportion2[key],
                      },
                    ],
                  });
                }
              }
            }
          }

          const sortedData = getSortedData(groupedData);
          setRowData(sortedData);
          dispatch(storeFouling(sortedData));
        } else {
          setRowData([]);
        }
      } catch (err) {
        setRowData([]);
      } finally {
        setIsLoading(false);
      }
    };
    fetchData();
  }, []);

  return (
    <div>
      <div className="descriptionText marginTop10 marginBtm10">
        {t("foulingStrings.foulingDescription")}
      </div>
      <div className="chart">
        <Charts
          cips={cips}
          date={date}
          cleaningResponse={cleaningResponse}
          threshold={threshold}
          activeStep={activeStep}
        />
      </div>
      {isLoading || !rowData ? (
        <WPLoader />
      ) : (
        <div className="ro-card-wrapper">
          <div className="ro-card-header">
            <div className="info-section">
              <div className="teal-heading">
                {t("foulingStrings.foulingAndScalingTypes")}
              </div>
              <IconWithTooltip
                label={t("helpInformation.foulingIdentificationTableHelp")}
              />
            </div>
          </div>

          <div className="ro-ag-table">
            <WPAgGrid
              gridRef={foulingTableRef}
              columnDefs={colDefs}
              rowData={rowData}
              exportFileName={t("foulingStrings.foulingAndScalingTypes")}
              disclaimerList={
                rowData.length > 0 ? [t("foulingStrings.scoreDescription")] : []
              }
            />
          </div>
          {rowData.length > 0 && (
            <div className="descriptionText marginTop10 marginBtm10">
              {t("foulingStrings.scoreDescription")}
            </div>
          )}
        </div>
      )}

      <hr />

      <div className="ro-disclaimer">{t("disclaimer", { currentYear })}</div>
    </div>
  );
};

export default FoulingIdentification;
